import {
  AriaAttributes,
  type RefObject,
  type FormEvent as ReactFormEvent,
  type ChangeEvent,
  type FocusEvent,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import {
  FormattedMessage,
  defineMessages,
  useIntl,
  type IntlShape,
} from 'react-intl';

import { useFormFieldMessagesContext } from './useFormFieldMessagesContext.js';
import { useId } from '../../../core/hooks/useId.js';
import { AriaLabelingProps } from '../../../core/types/a11y-props.js';
import { Text } from '../../../typography/text/Text.js';
import type {
  ValidationState,
  ValidationTrigger,
} from '../../validation/types.js';
import { useCustomMessageEmitter } from '../../validation/useCustomMessageEmitter.js';
import { getValidationState } from '../../validation/validation-builders.js';
import { FormFieldMessagesAction } from '../state/formfield-messages-state-reducer.js';

type FormFieldElement =
  | HTMLInputElement
  | HTMLSelectElement
  | HTMLTextAreaElement;

/**
 * Getter function to read the error type form the validity state.
 * The validity state values are readonly and can't therefore not be iterated but need to accessed directly instead.
 * https://stackoverflow.com/questions/20891950/html-validation-api-returns-an-empty-object-in-firefox?rq=2
 * @internal
 */
export function getErrorType(validityState?: ValidityState) {
  if (!validityState) {
    return null;
  }

  if (validityState.badInput) {
    return 'badInput';
  }
  if (validityState.patternMismatch) {
    return 'patternMismatch';
  }
  if (validityState.rangeOverflow) {
    return 'rangeOverflow';
  }
  if (validityState.rangeUnderflow) {
    return 'rangeUnderflow';
  }
  if (validityState.stepMismatch) {
    return 'stepMismatch';
  }
  if (validityState.tooLong) {
    return 'tooLong';
  }
  if (validityState.tooShort) {
    return 'tooShort';
  }
  if (validityState.typeMismatch) {
    return 'typeMismatch';
  }
  if (validityState.valueMissing) {
    return 'valueMissing';
  }
  /** We don't check for custom error since those should never be automatically updated. */
  return null;
}

const validationMessages = defineMessages({
  badInput: {
    id: '/cwiOkubC69MZmRu',
    defaultMessage: 'Please provide input in a valid format.',
    description:
      'Error message for input that the browser is unable to convert, e.g. string for a number input.',
  },
  patternMismatch: {
    id: 'qPeX7AgKbKzBFqGZ',
    defaultMessage: 'Please match the format requested.',
    description:
      "Error message for input that doesn't follow the requested pattern.",
  },
  rangeOverflow: {
    id: '14mVeqEFpqD2fpev',
    defaultMessage: 'Value must be less or equal to {max}.',
    description: 'Error message for input that value is higher then the max.',
  },
  rangeUnderflow: {
    id: '5XoVjJUQ5b86MLW+',
    defaultMessage: 'Value must be higher or equal to {min}.',
    description: 'Error message for input that value is lower then the max.',
  },
  stepMismatch: {
    id: 'MYfVMxPsAgOn8JSP',
    defaultMessage: 'Value must be a multiple of {step}, starting from {min}.',
    description:
      'Only values which are a whole number of steps from the step base are valid. The step base is min if specified, value otherwise, or 0 if neither is provided.',
  },
  tooLong: {
    id: 'JOIA3jQfIIkgnW04',
    defaultMessage:
      'Please shorten this text to {maxLength} characters or less.',
    description: 'Error message for input that is shorter than the maxLength.',
  },
  tooShort: {
    id: 'sJeuQTGrzcMvd6fY',
    defaultMessage:
      'Please lengthen this text to {minLength} characters or more.',
    description: 'Error message for input that is shorter than the minLength.',
  },
  typeMismatch: {
    id: 'fLfhKO8cXlo3vW5M',
    defaultMessage:
      'Please provide a value that matches the requested type ({type}).',
    description:
      "Error message for input that doesn't match the expected type.",
  },
  valueMissing: {
    id: 'laIylhVFbIvEZrxt',
    defaultMessage: 'Please fill in this field.',
    description: 'Error message for missing input.',
  },
});

/**
 * Internal getter function to return the matching formatted message for
 * each validityStateKey.
 * @internal
 */
export function getI18nErrorMessage(
  key: keyof ValidityState,
  ref?: FormFieldElement | null,
) {
  switch (key) {
    case 'badInput':
      return <FormattedMessage {...validationMessages.badInput} />;
    case 'patternMismatch':
      return <FormattedMessage {...validationMessages.patternMismatch} />;
    case 'rangeOverflow':
      return (
        <FormattedMessage
          {...validationMessages.rangeOverflow}
          values={{ max: ref?.getAttribute('max') }}
        />
      );
    case 'rangeUnderflow':
      return (
        <FormattedMessage
          {...validationMessages.rangeUnderflow}
          values={{ min: ref?.getAttribute('min') }}
        />
      );
    case 'stepMismatch':
      return (
        <FormattedMessage
          {...validationMessages.stepMismatch}
          values={{
            step: ref?.getAttribute('step'),
            min: ref?.getAttribute('min') ?? '0',
          }}
        />
      );
    case 'tooLong':
      return (
        <FormattedMessage
          {...validationMessages.tooLong}
          values={{ maxLength: ref?.getAttribute('maxLength') }}
        />
      );
    case 'tooShort':
      return (
        <FormattedMessage
          {...validationMessages.tooShort}
          values={{ minLength: ref?.getAttribute('minLength') }}
        />
      );
    case 'typeMismatch':
      return (
        <FormattedMessage
          {...validationMessages.typeMismatch}
          values={{ type: ref?.getAttribute('type') }}
        />
      );
    case 'valueMissing':
      return <FormattedMessage {...validationMessages.valueMissing} />;
    case 'customError':
    case 'valid':
    default:
      return null;
  }
}

/**
 * Returns the translated plain-string validation message for a given validity
 * state key. Mirrors the JSX messages in {@link getI18nErrorMessage} using the
 * same descriptors so both the UI and `ValidationError.message` stay in sync.
 *
 * @internal
 */
export function getI18nValidationMessage(
  intl: IntlShape,
  key: keyof ValidityState,
  ref?: FormFieldElement | null,
): string {
  switch (key) {
    case 'badInput':
      return intl.formatMessage(validationMessages.badInput);
    case 'patternMismatch':
      return intl.formatMessage(validationMessages.patternMismatch);
    case 'rangeOverflow':
      return intl.formatMessage(validationMessages.rangeOverflow, {
        max: ref?.getAttribute('max'),
      });
    case 'rangeUnderflow':
      return intl.formatMessage(validationMessages.rangeUnderflow, {
        min: ref?.getAttribute('min'),
      });
    case 'stepMismatch':
      return intl.formatMessage(validationMessages.stepMismatch, {
        step: ref?.getAttribute('step'),
        min: ref?.getAttribute('min') ?? '0',
      });
    case 'tooLong':
      return intl.formatMessage(validationMessages.tooLong, {
        maxLength: ref?.getAttribute('maxLength'),
      });
    case 'tooShort':
      return intl.formatMessage(validationMessages.tooShort, {
        minLength: ref?.getAttribute('minLength'),
      });
    case 'typeMismatch':
      return intl.formatMessage(validationMessages.typeMismatch, {
        type: ref?.getAttribute('type'),
      });
    case 'valueMissing':
      return intl.formatMessage(validationMessages.valueMissing);
    default:
      return '';
  }
}

/**
 * Builds a ValidationState with an i18n message for the current validity of
 * `element`, bypassing the corrupted `element.validationMessage` that
 * `setCustomValidity` produces.
 * @internal
 */
export function getI18nValidationState(
  intl: IntlShape,
  element: FormFieldElement | null | undefined,
  trigger: ValidationTrigger,
): ValidationState {
  const errorType = getErrorType(element?.validity);
  return getValidationState(
    element,
    trigger,
    errorType ? getI18nValidationMessage(intl, errorType, element) : undefined,
  );
}

/** @internal */
interface FormFieldValidationProps {
  ariaProps: Pick<
    AriaAttributes,
    'aria-errormessage' | 'aria-invalid' | 'aria-describedby'
  >;
  onInvalid: (event: ReactFormEvent<FormFieldElement>) => void;
  onChange: (event?: ChangeEvent<FormFieldElement>) => void;
  onBlur: (event?: FocusEvent<FormFieldElement>) => void;
}

/**
 * Hook to generate ARIA props based on the form field messages
 * @param ariaLabelingProps - ARIA labeling properties
 * @returns ARIA props for the form field
 * @internal
 */
export function useFormFieldValidationAriaProps(
  ariaLabelingProps: AriaLabelingProps = {},
) {
  const { state } = useFormFieldMessagesContext();

  const _ariaProps = useMemo(() => {
    let messageIdsString;
    let errorMessageIdsString;

    if (
      state.customMessages.length > 0 ||
      state.formControlMessages.length > 0
    ) {
      const messages = [...state.customMessages, ...state.formControlMessages];
      errorMessageIdsString = messages
        .filter((msg) => msg.variant === 'error')
        .map((msg) => msg.id)
        .join(' ');

      messageIdsString = messages.map((msg) => msg.id).join(' ');
    }

    return {
      'aria-errormessage': state.showError ? errorMessageIdsString : undefined,
      'aria-invalid': state.showError,
      'aria-describedby': messageIdsString,
    };
  }, [state.customMessages, state.formControlMessages, state.showError]);
  const ariaProps = getCombinedAriaProps(_ariaProps, ariaLabelingProps);

  return ariaProps;
}

/**
 * @param ref - Reference to the input element
 * @param validationDeps - Values that trigger re-validation when changed (e.g., [required, min, max, pattern]).
 * @param ariaLabelingProps - ARIA labeling properties
 * @param controlId - Optional control ID for message management
 * @internal
 */
export function useFormFieldValidation(
  ref: RefObject<FormFieldElement | null>,
  validationDeps: Record<string, string | number | boolean | undefined> = {},
  ariaLabelingProps: AriaLabelingProps = {},
  controlId?: string,
  emitValidationStateProp?: (state: ValidationState) => void,
): FormFieldValidationProps {
  const { state, dispatch } = useFormFieldMessagesContext();
  const intl = useIntl();
  const errorId = useId();

  const ariaProps = useFormFieldValidationAriaProps(ariaLabelingProps);

  // Keep a ref to the current formControlMessages so `updateErrorMessages`
  // can read the latest list without listing it as a reactive dependency.
  // If it were a dependency, any dispatch (add/remove message) would recreate
  // the callback, re-trigger the useEffect below, which would dispatch again —
  // causing an infinite loop.
  const formControlMessagesRef = useRef(state.formControlMessages);
  formControlMessagesRef.current = state.formControlMessages;

  const updateErrorMessages = useCallback(() => {
    const errorType = getErrorType(ref.current?.validity);

    formControlMessagesRef.current.forEach((message) => {
      if (
        message.validityStateKey !== errorType &&
        message.validityStateKey !== 'customError'
      ) {
        dispatch?.({
          type: FormFieldMessagesAction.REMOVEMESSAGE,
          payload: {
            id: message.id,
            messageType: 'formcontrol',
            controlId,
          },
        });
      }
    });

    if (errorType) {
      dispatch?.({
        type: FormFieldMessagesAction.ADDMESSAGE,
        payload: {
          id: errorId,
          message: (
            <Text textStyle="small" as="p">
              {getI18nErrorMessage(errorType, ref.current)}
            </Text>
          ),
          validityStateKey: errorType,
          messageType: 'formcontrol',
          variant: 'error',
          controlId,
        },
      });
    }
  }, [controlId, dispatch, errorId, ref]); // formControlMessages intentionally excluded — read via ref above

  const onInvalid = useCallback(
    (event: ReactFormEvent<FormFieldElement>) => {
      updateErrorMessages();

      dispatch?.({
        type: FormFieldMessagesAction.SETISTOUCHED,
        payload: { isTouched: true },
      });

      // Prevents the browser validation pop up.
      event.preventDefault();
    },
    [dispatch, updateErrorMessages],
  );

  const emitValidationState = useCustomMessageEmitter(
    emitValidationStateProp,
    () => getI18nValidationState(intl, ref.current, 'manual'),
  );

  const onBlur = useCallback(() => {
    ref.current?.reportValidity();
    dispatch?.({
      type: FormFieldMessagesAction.SETISTOUCHED,
      payload: { isTouched: true },
    });
    if (!state.isTouched) {
      emitValidationState(getI18nValidationState(intl, ref.current, 'blur'));
    }
  }, [dispatch, emitValidationState, intl, ref, state.isTouched]);

  const onChange = useCallback(() => {
    updateErrorMessages();
    emitValidationState(getI18nValidationState(intl, ref.current, 'change'));
  }, [updateErrorMessages, emitValidationState, intl, ref]);

  const validationDepsKey = JSON.stringify(validationDeps);

  useEffect(() => {
    onChange();
  }, [validationDepsKey, onChange]);

  return {
    ariaProps,
    onInvalid,
    onChange,
    onBlur,
  };
}

/* @internal - function to combine aria-labelling and validation aria-props correctly. */
export function getCombinedAriaProps(
  validationAriaProps: Pick<
    AriaAttributes,
    'aria-errormessage' | 'aria-invalid' | 'aria-describedby'
  >,
  labelingProps: AriaLabelingProps,
) {
  const {
    'aria-describedby': validationDescription,
    ...remainingValidationProps
  } = validationAriaProps;
  const { 'aria-describedby': propDescription, ...remainingLabelingProps } =
    labelingProps;

  if (validationDescription && propDescription) {
    return {
      ...remainingLabelingProps,
      ...remainingValidationProps,
      'aria-describedby': (
        validationDescription +
        ' ' +
        propDescription
      ).trim(),
    };
  }

  return {
    ...remainingLabelingProps,
    ...remainingValidationProps,
    'aria-describedby': validationDescription ?? propDescription,
  };
}
